<template>
  <div>
    <!-- Page Header -->
    <b-card>
      <b-row>
        <b-col
          cols="12"
          md="8"
        >
          <h3 class="mb-1">
            استيراد أوامر الرفع المساحي
          </h3>
          <b-card-text class="mb-0">
            استيراد من ملف Excel أو من بوابة الرفع المساحي. البيانات الثابتة أدناه تُطبَّق على المشاريع المنشأة.
          </b-card-text>
        </b-col>
        <b-col
          v-if="canImportFromPortal"
          cols="12"
          md="4"
          class="d-flex justify-content-end align-items-center mt-1 mt-md-0"
        >
          <b-button
            v-b-tooltip.hover.top="'Import from portal'"
            variant="outline-primary"
            :disabled="portalImportRunning || portalSubmitting"
            @click="openPortalImportModal"
          >
            <feather-icon
              icon="GlobeIcon"
              class="mr-50"
            />
            استيراد من البوابة
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-alert
      v-if="portalImportRunning"
      show
      variant="info"
      class="mt-2 mb-0"
    >
      جاري الاستيراد من البوابة في الخلفية
      <span v-if="portalQueuedTotal">
        ({{ portalQueuedTotal }} سجل).
      </span>
      يمكنك متابعة استخدام الصفحة؛ سيصلك إشعار عند الانتهاء.
    </b-alert>

    <!-- Import Form -->
    <b-card class="mt-2">
      <b-card-title>رفع ملف Excel</b-card-title>

      <b-form @submit.prevent="handleImport">
        <b-row>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              label="اختر الملف"
              label-for="file-input"
            >
              <b-form-file
                id="file-input"
                v-model="importFile"
                :state="fileState"
                placeholder="اختر ملف Excel (.xlsx أو .xls)"
                drop-placeholder="اسحب الملف هنا"
                accept=".xlsx,.xls"
                :disabled="loading"
                @change="handleFileChange"
              />
              <b-form-invalid-feedback v-if="fileError">
                {{ fileError }}
              </b-form-invalid-feedback>
              <b-form-text v-if="importFile && !fileError">
                الملف المحدد: {{ importFile.name }} ({{ formatFileSize(importFile.size) }})
              </b-form-text>
            </b-form-group>
          </b-col>

          <!-- <b-col cols="12" md="6">
            <b-form-group label="Dry run (استيراد بدون إنشاء مشاريع)" label-for="dry-run">
              <b-form-checkbox
                id="dry-run"
                v-model="dryRun"
                switch
                :disabled="loading"
              >
                تفعيل Dry run
              </b-form-checkbox>
              <b-form-text>
                عند التفعيل سيتم فحص/استيراد البيانات فقط بدون إنشاء/ربط مشاريع.
              </b-form-text>
            </b-form-group>
          </b-col> -->
        </b-row>

        <b-card
          class="mt-1"
          body-class="p-1"
        >
          <b-card-title class="mb-1">
            البيانات الثابتة (تُطبق على كل المشاريع)
          </b-card-title>

          <b-row>
            <b-col
              cols="12"
              md="4"
            >

              <b-form-group
                label="المهندس المسؤول"
                label-for="technical_responsible_id"
              >
                <v-select
                  id="technical_responsible_id"
                  v-model="staticFields.technical_responsible_id"
                  :options="employeesOptions"
                  :reduce="val => val.id"
                  label="name"
                  dir="rtl"
                  :clearable="true"
                  :disabled="loading"
                  :get-option-label="option => option.full_name"
                  @search="onEmployeeSearch"
                />
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="4"
            >
              <b-form-group
                label="المسؤول عن الاتفاقية"
                label-for="office_agreement_responsible_id"
              >
                <v-select
                  id="office_agreement_responsible_id"
                  v-model="staticFields.office_agreement_responsible_id"
                  :options="employeesOptions"
                  :reduce="val => val.id"
                  label="name"
                  dir="rtl"
                  :clearable="true"
                  :get-option-label="option => option.full_name"
                  :disabled="loading"
                  @search="onEmployeeSearch"
                />
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="4"
            >
              <b-form-group
                label="المسؤول عن الرسم"
                label-for="office_drawing_responsible_id"
              >
                <v-select
                  id="office_drawing_responsible_id"
                  v-model="staticFields.office_drawing_responsible_id"
                  :options="employeesOptions"
                  :reduce="val => val.id"
                  label="name"
                  dir="rtl"
                  :clearable="true"
                  :disabled="loading"
                  :get-option-label="option => option.full_name"
                  @search="onEmployeeSearch"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label="فريق المعاينة"
                label-for="inspection_teams"
              >
                <v-select
                  id="inspection_teams"
                  v-model="staticFields.inspection_teams"
                  :options="employeesOptions"
                  :reduce="val => val.id"
                  label="name"
                  dir="rtl"
                  multiple
                  :close-on-select="false"
                  :clearable="true"
                  :disabled="loading"
                  :get-option-label="option => option.full_name"
                  @search="onEmployeeSearch"
                />
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="3"
            >
              <b-form-group
                label="التكلفة الفعلية"
                label-for="actual_cost"
              >
                <b-form-input
                  id="actual_cost"
                  v-model="staticFields.actual_cost"
                  type="number"
                  min="0"
                  step="0.01"
                  :disabled="loading"
                  placeholder="مثال: 1500"
                />
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="3"
            >
              <b-form-group
                label="تكلفة السائق"
                label-for="driver_cost"
              >
                <b-form-input
                  id="driver_cost"
                  v-model="staticFields.driver_cost"
                  type="number"
                  min="0"
                  step="0.01"
                  :disabled="loading"
                  placeholder="مثال: 200"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label="عميل مفوض بالحساب"
                label-for="authorized_client_id"
              >
                <v-select
                  id="authorized_client_id"
                  v-model="staticFields.authorized_client_id"
                  :options="customersOptions"
                  :reduce="val => val.id"
                  label="name"
                  dir="rtl"
                  :clearable="true"
                  :disabled="loading"
                  @search="onCustomerSearch"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>

        <b-row class="mt-2">
          <b-col cols="12">
            <b-button
              type="submit"
              variant="primary"
              :disabled="!importFile || loading || !!fileError"
              class="mr-1"
            >
              <feather-icon
                v-if="!loading"
                icon="UploadIcon"
                class="mr-50"
              />
              <b-spinner
                v-else
                small
                class="mr-50"
              />
              <span v-if="!loading">استيراد وإنشاء المشاريع</span>
              <span v-else>جاري الاستيراد...</span>
            </b-button>

            <b-button
              variant="outline-secondary"
              :disabled="loading"
              @click="resetForm"
            >
              إعادة تعيين
            </b-button>

            <b-button
              v-if="canImportFromPortal"
              v-b-tooltip.hover.top="'Import from portal'"
              variant="outline-primary"
              class="ml-1"
              :disabled="loading || portalImportRunning || portalSubmitting"
              @click="openPortalImportModal"
            >
              <feather-icon
                icon="GlobeIcon"
                class="mr-50"
              />
              استيراد من البوابة
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-card>

    <!-- Results -->
    <b-card
      v-if="importResult"
      class="mt-2"
    >
      <b-card-title>
        <feather-icon
          :icon="importResult.success ? 'CheckCircleIcon' : 'AlertCircleIcon'"
          :class="importResult.success ? 'text-success' : 'text-danger'"
          class="mr-50"
        />
        نتائج الاستيراد
      </b-card-title>

      <div v-if="importResult.success && importResult.data">
        <!-- Import Summary -->
        <h5 class="mt-1 mb-1">
          ملخص الاستيراد
        </h5>
        <b-row class="mb-1">
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-primary">
              <h4 class="mb-0">
                {{ importSummary.total || 0 }}
              </h4>
              <small>إجمالي الصفوف</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-secondary">
              <h4 class="mb-0">
                {{ importSummary.processed || 0 }}
              </h4>
              <small>تم المعالجة</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-success">
              <h4 class="mb-0">
                {{ importSummary.imported || 0 }}
              </h4>
              <small>تم الاستيراد</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-info">
              <h4 class="mb-0">
                {{ importSummary.updated || 0 }}
              </h4>
              <small>تم التحديث</small>
            </b-card>
          </b-col>
        </b-row>

        <b-row class="mb-2">
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-warning">
              <h4 class="mb-0">
                {{ importSummary.skipped || 0 }}
              </h4>
              <small>تم التخطي</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-danger">
              <h4 class="mb-0">
                {{ importSummary.errors || 0 }}
              </h4>
              <small>أخطاء</small>
            </b-card>
          </b-col>
        </b-row>

        <!-- Projects Summary -->
        <h5 class="mt-2 mb-1">
          ملخص إنشاء المشاريع
        </h5>
        <b-row class="mb-1">
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-secondary">
              <h4 class="mb-0">
                {{ projectsSummary.processed || 0 }}
              </h4>
              <small>تم المعالجة</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-success">
              <h4 class="mb-0">
                {{ projectsSummary.created || 0 }}
              </h4>
              <small>تم الإنشاء</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-info">
              <h4 class="mb-0">
                {{ projectsSummary.linked || 0 }}
              </h4>
              <small>تم الربط</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-warning">
              <h4 class="mb-0">
                {{ projectsSummary.skipped || 0 }}
              </h4>
              <small>تم التخطي</small>
            </b-card>
          </b-col>
        </b-row>

        <b-row class="mb-2">
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-danger">
              <h4 class="mb-0">
                {{ projectsSummary.errors || 0 }}
              </h4>
              <small>أخطاء</small>
            </b-card>
          </b-col>
        </b-row>

        <!-- Error Details Table -->
        <div v-if="combinedErrorDetails.length > 0">
          <h5 class="mt-2 mb-1 text-danger">
            جدول الأخطاء
          </h5>
          <b-table
            :items="combinedErrorDetails"
            :fields="errorFields"
            responsive
            striped
            hover
            small
          >
            <template #cell(source)="data">
              <b-badge :variant="data.value === 'projects' ? 'info' : 'warning'">
                {{ data.value === 'projects' ? 'مشاريع' : 'استيراد' }}
              </b-badge>
            </template>
            <template #cell(row)="data">
              <b-badge variant="warning">
                صف {{ data.value }}
              </b-badge>
            </template>
            <template #cell(error)="data">
              <span class="text-danger">{{ data.value }}</span>
            </template>
          </b-table>
        </div>
      </div>

      <div
        v-else
        class="text-danger"
      >
        {{ importResult.error || 'حدث خطأ أثناء الاستيراد' }}
      </div>
    </b-card>

    <!-- Portal import report (from completion notification) -->
    <b-card
      v-if="portalImportFailure"
      class="mt-2"
    >
      <b-card-title>
        <feather-icon
          icon="AlertCircleIcon"
          class="text-danger mr-50"
        />
        فشل الاستيراد من البوابة
      </b-card-title>
      <b-alert
        show
        variant="danger"
        class="mb-0"
      >
        {{ portalImportFailure }}
      </b-alert>
    </b-card>

    <b-card
      v-if="portalReport"
      class="mt-2"
    >
      <b-card-title>
        <feather-icon
          :icon="portalReportHasErrors ? 'AlertCircleIcon' : 'CheckCircleIcon'"
          :class="portalReportHasErrors ? 'text-warning' : 'text-success'"
          class="mr-50"
        />
        {{ portalReportTitle || 'تقرير الاستيراد من البوابة' }}
      </b-card-title>

      <p
        v-if="portalReportMessage"
        class="mb-1"
      >
        {{ portalReportMessage }}
      </p>
      <p
        v-if="portalReport.duration_seconds"
        class="text-muted"
      >
        المدة: {{ formatDuration(portalReport.duration_seconds) }}
      </p>

      <b-row>
        <b-col
          cols="12"
          lg="4"
          class="mb-1"
        >
          <b-card class="h-100">
            <b-card-title>الطلبات</b-card-title>
            <b-row>
              <b-col cols="6">
                <b-card class="text-center bg-light-success mb-1">
                  <h4 class="mb-0">
                    {{ portalOrders.imported || 0 }}
                  </h4>
                  <small>تم الاستيراد</small>
                </b-card>
              </b-col>
              <b-col cols="6">
                <b-card class="text-center bg-light-info mb-1">
                  <h4 class="mb-0">
                    {{ portalOrders.updated || 0 }}
                  </h4>
                  <small>تم التحديث</small>
                </b-card>
              </b-col>
              <b-col cols="6">
                <b-card class="text-center bg-light-warning mb-1">
                  <h4 class="mb-0">
                    {{ portalOrders.skipped || 0 }}
                  </h4>
                  <small>تم التخطي</small>
                </b-card>
              </b-col>
              <b-col cols="6">
                <b-card class="text-center bg-light-primary mb-1">
                  <h4 class="mb-0">
                    {{ portalOrders.total || 0 }}
                  </h4>
                  <small>الإجمالي</small>
                </b-card>
              </b-col>
              <b-col cols="12">
                <b-card class="text-center bg-light-danger">
                  <h4 class="mb-0">
                    {{ portalOrders.errors || 0 }}
                  </h4>
                  <small>أخطاء</small>
                </b-card>
              </b-col>
            </b-row>
            <div
              v-if="portalOrdersErrorDetails.count > 0"
              class="mt-1"
            >
              <small class="text-danger d-block mb-50">تفاصيل أخطاء الطلبات</small>
              <ul class="pl-1 mb-50">
                <li
                  v-for="(item, idx) in portalOrdersErrorDetails.items"
                  :key="`order-err-${idx}`"
                  class="text-danger small"
                >
                  صف {{ item.row }}
                  <span v-if="item.order_number">({{ item.order_number }})</span>
                  : {{ item.error }}
                </li>
              </ul>
              <small
                v-if="portalOrdersErrorDetails.truncated"
                class="text-muted"
              >
                توجد أخطاء إضافية غير معروضة.
              </small>
            </div>
          </b-card>
        </b-col>

        <b-col
          cols="12"
          lg="4"
          class="mb-1"
        >
          <b-card class="h-100">
            <b-card-title>المشاريع</b-card-title>
            <b-row>
              <b-col cols="6">
                <b-card class="text-center bg-light-success mb-1">
                  <h4 class="mb-0">
                    {{ portalProjects.created || 0 }}
                  </h4>
                  <small>تم الإنشاء</small>
                </b-card>
              </b-col>
              <b-col cols="6">
                <b-card class="text-center bg-light-info mb-1">
                  <h4 class="mb-0">
                    {{ portalProjects.linked || 0 }}
                  </h4>
                  <small>تم الربط</small>
                </b-card>
              </b-col>
              <b-col cols="6">
                <b-card class="text-center bg-light-warning mb-1">
                  <h4 class="mb-0">
                    {{ portalProjects.skipped || 0 }}
                  </h4>
                  <small>تم التخطي</small>
                </b-card>
              </b-col>
              <b-col cols="6">
                <b-card class="text-center bg-light-danger mb-1">
                  <h4 class="mb-0">
                    {{ portalProjects.errors || 0 }}
                  </h4>
                  <small>أخطاء</small>
                </b-card>
              </b-col>
            </b-row>
            <div
              v-if="portalProjectsErrorDetails.count > 0"
              class="mt-1"
            >
              <small class="text-danger d-block mb-50">تفاصيل أخطاء المشاريع</small>
              <ul class="pl-1 mb-50">
                <li
                  v-for="(item, idx) in portalProjectsErrorDetails.items"
                  :key="`project-err-${idx}`"
                  class="text-danger small"
                >
                  صف {{ item.row }}
                  <span v-if="item.order_number">({{ item.order_number }})</span>
                  : {{ item.error }}
                </li>
              </ul>
              <small
                v-if="portalProjectsErrorDetails.truncated"
                class="text-muted"
              >
                توجد أخطاء إضافية غير معروضة.
              </small>
            </div>
          </b-card>
        </b-col>

        <b-col
          cols="12"
          lg="4"
          class="mb-1"
        >
          <b-card class="h-100">
            <b-card-title>الجيودسيا</b-card-title>
            <b-row>
              <b-col cols="6">
                <b-card class="text-center bg-light-success mb-1">
                  <h4 class="mb-0">
                    {{ portalGeodesias.created || 0 }}
                  </h4>
                  <small>تم الإنشاء</small>
                </b-card>
              </b-col>
              <b-col cols="6">
                <b-card class="text-center bg-light-info mb-1">
                  <h4 class="mb-0">
                    {{ portalGeodesias.updated || 0 }}
                  </h4>
                  <small>تم التحديث</small>
                </b-card>
              </b-col>
              <b-col cols="6">
                <b-card class="text-center bg-light-warning mb-1">
                  <h4 class="mb-0">
                    {{ portalGeodesias.skipped || 0 }}
                  </h4>
                  <small>تم التخطي</small>
                </b-card>
              </b-col>
              <b-col cols="6">
                <b-card class="text-center bg-light-danger mb-1">
                  <h4 class="mb-0">
                    {{ portalGeodesias.errors || 0 }}
                  </h4>
                  <small>أخطاء</small>
                </b-card>
              </b-col>
            </b-row>
            <div
              v-if="portalGeodesiasErrorDetails.count > 0"
              class="mt-1"
            >
              <small class="text-danger d-block mb-50">تفاصيل أخطاء الجيودسيا</small>
              <ul class="pl-1 mb-50">
                <li
                  v-for="(item, idx) in portalGeodesiasErrorDetails.items"
                  :key="`geo-err-${idx}`"
                  class="text-danger small"
                >
                  صف {{ item.row }}
                  <span v-if="item.order_number">({{ item.order_number }})</span>
                  : {{ item.error }}
                </li>
              </ul>
              <small
                v-if="portalGeodesiasErrorDetails.truncated"
                class="text-muted"
              >
                توجد أخطاء إضافية غير معروضة.
              </small>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </b-card>

    <b-modal
      id="portal-import-modal"
      ref="portalImportModal"
      v-model="showPortalImportModal"
      title="استيراد من البوابة"
      size="lg"
      ok-title="تأكيد الاستيراد"
      cancel-title="إلغاء"
      :ok-disabled="!portalSessionId.trim() || portalSubmitting || portalImportRunning"
      no-close-on-backdrop
      @ok.prevent="handlePortalImport"
      @hidden="clearPortalSessionId"
    >
      <b-alert
        show
        variant="warning"
        class="mb-1"
      >
        سيتم سحب قائمة البوابة بالكامل وإنشاء/تحديث أوامر الرفع المساحي والمشاريع في الخلفية. قد يستغرق ذلك عدة دقائق. لا تُحفظ قيمة الجلسة بعد الإرسال.
      </b-alert>

      <b-form-group
        label="معرّف جلسة البوابة (sessionid)"
        label-for="portal-sessionid"
      >
        <b-form-input
          id="portal-sessionid"
          v-model="portalSessionId"
          type="password"
          autocomplete="off"
          autocapitalize="off"
          spellcheck="false"
          placeholder="الصق sessionid من تبويب Network"
          :disabled="portalSubmitting"
        />
        <b-form-text>
          انسخ القيمة من landsurvey-eg.com → Network → طلب PageData → query sessionid. الجلسة تنتهي؛ الصق قيمة جديدة في كل استيراد.
        </b-form-text>
      </b-form-group>

      <p class="text-muted small mb-50">
        الحقول الاختيارية التالية تُطبَّق على المشاريع المنشأة (نفس حقول استيراد Excel).
      </p>

      <b-row>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group
            label="المهندس المسؤول"
            label-for="portal_technical_responsible_id"
          >
            <v-select
              id="portal_technical_responsible_id"
              v-model="staticFields.technical_responsible_id"
              :options="employeesOptions"
              :reduce="val => val.id"
              label="name"
              dir="rtl"
              :clearable="true"
              :disabled="portalSubmitting"
              :get-option-label="option => option.full_name"
              @search="onEmployeeSearch"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group
            label="المسؤول عن الاتفاقية"
            label-for="portal_office_agreement_responsible_id"
          >
            <v-select
              id="portal_office_agreement_responsible_id"
              v-model="staticFields.office_agreement_responsible_id"
              :options="employeesOptions"
              :reduce="val => val.id"
              label="name"
              dir="rtl"
              :clearable="true"
              :disabled="portalSubmitting"
              :get-option-label="option => option.full_name"
              @search="onEmployeeSearch"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group
            label="المسؤول عن الرسم"
            label-for="portal_office_drawing_responsible_id"
          >
            <v-select
              id="portal_office_drawing_responsible_id"
              v-model="staticFields.office_drawing_responsible_id"
              :options="employeesOptions"
              :reduce="val => val.id"
              label="name"
              dir="rtl"
              :clearable="true"
              :disabled="portalSubmitting"
              :get-option-label="option => option.full_name"
              @search="onEmployeeSearch"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            label="فريق المعاينة"
            label-for="portal_inspection_teams"
          >
            <v-select
              id="portal_inspection_teams"
              v-model="staticFields.inspection_teams"
              :options="employeesOptions"
              :reduce="val => val.id"
              label="name"
              dir="rtl"
              multiple
              :close-on-select="false"
              :clearable="true"
              :disabled="portalSubmitting"
              :get-option-label="option => option.full_name"
              @search="onEmployeeSearch"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="التكلفة الفعلية"
            label-for="portal_actual_cost"
          >
            <b-form-input
              id="portal_actual_cost"
              v-model="staticFields.actual_cost"
              type="number"
              min="0"
              step="0.01"
              :disabled="portalSubmitting"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="تكلفة السائق"
            label-for="portal_driver_cost"
          >
            <b-form-input
              id="portal_driver_cost"
              v-model="staticFields.driver_cost"
              type="number"
              min="0"
              step="0.01"
              :disabled="portalSubmitting"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            label="عميل مفوض بالحساب"
            label-for="portal_authorized_client_id"
          >
            <v-select
              id="portal_authorized_client_id"
              v-model="staticFields.authorized_client_id"
              :options="customersOptions"
              :reduce="val => val.id"
              label="name"
              dir="rtl"
              :clearable="true"
              :disabled="portalSubmitting"
              @search="onCustomerSearch"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardText,
  BCardTitle,
  BRow,
  BCol,
  BButton,
  BForm,
  BFormGroup,
  BFormFile,
  BFormText,
  BFormInvalidFeedback,
  BFormInput,
  BFormCheckbox,
  BTable,
  BBadge,
  BSpinner,
  BAlert,
  BModal,
  VBTooltip,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import landsurveyOrdersAPI from '@/api/landsurvey-orders/landsurveyOrders'
import vSelect from 'vue-select'

const PORTAL_COMPLETED_TYPE = 'landsurvey_portal_import_completed'
const PORTAL_FAILED_TYPE = 'external_request_import_failed'

export default {
  name: 'LandSurveyOrdersImport',
  components: {
    BCard,
    vSelect,
    BCardText,
    BCardTitle,
    BRow,
    BCol,
    BButton,
    BForm,
    BFormGroup,
    BFormFile,
    BFormText,
    BFormInvalidFeedback,
    BFormInput,
    BFormCheckbox,
    BTable,
    BBadge,
    BSpinner,
    BAlert,
    BModal,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      importFile: null,
      dryRun: false,
      loading: false,
      fileError: null,
      importResult: null,
      staticFields: {
        technical_responsible_id: null,
        office_agreement_responsible_id: null,
        office_drawing_responsible_id: null,
        inspection_teams: [],
        actual_cost: null,
        driver_cost: null,
        authorized_client_id: null,
      },
      errorFields: [
        { key: 'source', label: 'النوع', sortable: true },
        { key: 'row', label: 'رقم الصف', sortable: true },
        { key: 'error', label: 'الخطأ', sortable: false },
      ],
      searchTimeout: null,
      showPortalImportModal: false,
      portalSessionId: '',
      portalSubmitting: false,
      portalImportRunning: false,
      portalQueuedTotal: null,
      portalReport: null,
      portalReportTitle: '',
      portalReportMessage: '',
      portalImportFailure: null,
      processedPortalNotificationIds: {},
    }
  },
  computed: {
    employees() {
      return this.$store.getters['employees/employees']
    },
    customers() {
      return this.$store.getters['customers/customers']
    },
    employeesOptions() {
      return (this.employees && this.employees.data) ? this.employees.data : []
    },
    customersOptions() {
      return (this.customers && this.customers.data) ? this.customers.data : []
    },
    fileState() {
      if (this.fileError) return false
      if (this.importFile) return true
      return null
    },
    importSummary() {
      return this.importResult?.data?.import || {}
    },
    projectsSummary() {
      return this.importResult?.data?.projects || {}
    },
    combinedErrorDetails() {
      const importErrors = Array.isArray(this.importSummary.error_details)
        ? this.importSummary.error_details.map(e => ({ source: 'import', row: e.row, error: e.error }))
        : []
      const projectErrors = Array.isArray(this.projectsSummary.error_details)
        ? this.projectsSummary.error_details.map(e => ({ source: 'projects', row: e.row, error: e.error }))
        : []
      return [...importErrors, ...projectErrors]
    },
    canImportFromPortal() {
      return this.hasPermission('create landsurvey_orders') && this.hasPermission('create projects')
    },
    notificationFeed() {
      const items = this.$store.getters['notifications/items'] || []
      const items168 = this.$store.getters['notifications/items_168'] || []
      return [...items, ...items168]
    },
    portalOrders() {
      return (this.portalReport && this.portalReport.import) || {}
    },
    portalProjects() {
      return (this.portalReport && this.portalReport.projects) || {}
    },
    portalGeodesias() {
      return (this.portalReport && this.portalReport.geodesias) || {}
    },
    portalOrdersErrorDetails() {
      return this.normalizeErrorDetails(this.portalOrders.error_details)
    },
    portalProjectsErrorDetails() {
      return this.normalizeErrorDetails(this.portalProjects.error_details)
    },
    portalGeodesiasErrorDetails() {
      return this.normalizeErrorDetails(this.portalGeodesias.error_details)
    },
    portalReportHasErrors() {
      return (this.portalOrders.errors || 0) > 0
        || (this.portalProjects.errors || 0) > 0
        || (this.portalGeodesias.errors || 0) > 0
        || this.portalOrdersErrorDetails.count > 0
        || this.portalProjectsErrorDetails.count > 0
        || this.portalGeodesiasErrorDetails.count > 0
    },
  },
  watch: {
    notificationFeed: {
      handler(items) {
        this.consumePortalImportNotifications(items)
      },
      immediate: true,
    },
  },
  mounted() {
    this.$store.dispatch('employees/listEmployees', { query: {} })
    this.$store.dispatch('customers/listCustomers', { query: {} })
    this.clearStalePortalImportHint()
  },
  beforeDestroy() {
    this.clearPortalSessionId()
  },
  methods: {
    handleFileChange(event) {
      this.fileError = null
      this.importResult = null

      const file = event?.target?.files?.[0]
      if (!file) {
        this.importFile = null
        return
      }

      const validExtensions = ['.xlsx', '.xls']
      const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`
      if (!validExtensions.includes(fileExtension)) {
        this.fileError = 'يجب أن يكون الملف من نوع Excel (.xlsx أو .xls)'
        this.importFile = null
        return
      }

      const maxSize = 15 * 1024 * 1024
      if (file.size > maxSize) {
        this.fileError = 'حجم الملف يجب أن يكون أقل من 15 ميجابايت'
        this.importFile = null
        return
      }

      this.importFile = file
    },

    onEmployeeSearch(search, loading) {
      clearTimeout(this.searchTimeout)
      if (loading) loading(true)
      this.searchTimeout = setTimeout(() => {
        const query = search ? { search } : {}
        this.$store.dispatch('employees/listEmployees', { query }).finally(() => {
          if (loading) loading(false)
        })
      }, 300)
    },

    onCustomerSearch(search, loading) {
      clearTimeout(this.searchTimeout)
      if (loading) loading(true)
      this.searchTimeout = setTimeout(() => {
        const query = search ? { search } : {}
        this.$store.dispatch('customers/listCustomers', { query }).finally(() => {
          if (loading) loading(false)
        })
      }, 300)
    },

    async handleImport() {
      if (!this.importFile || this.fileError) {
        this.showError('يرجى اختيار ملف صحيح')
        return
      }

      this.loading = true
      this.importResult = null

      try {
        const formData = new FormData()
        formData.append('file', this.importFile)

        if (this.dryRun) {
          formData.append('dry_run', '1')
        }

        // Static fields (optional)
        const s = this.staticFields
        if (s.technical_responsible_id) formData.append('technical_responsible_id', String(s.technical_responsible_id))
        if (s.office_agreement_responsible_id) formData.append('office_agreement_responsible_id', String(s.office_agreement_responsible_id))
        if (s.office_drawing_responsible_id) formData.append('office_drawing_responsible_id', String(s.office_drawing_responsible_id))
        if (s.authorized_client_id) formData.append('authorized_client_id', String(s.authorized_client_id))

        if (Array.isArray(s.inspection_teams) && s.inspection_teams.length > 0) {
          s.inspection_teams.forEach(id => {
            if (id) formData.append('inspection_teams[]', String(id))
          })
        }

        const hasActualCost = s.actual_cost !== null && s.actual_cost !== '' && typeof s.actual_cost !== 'undefined'
        const hasDriverCost = s.driver_cost !== null && s.driver_cost !== '' && typeof s.driver_cost !== 'undefined'
        if (hasActualCost) formData.append('actual_cost', String(s.actual_cost))
        if (hasDriverCost) formData.append('driver_cost', String(s.driver_cost))

        const response = await landsurveyOrdersAPI.import(formData)

        this.importResult = {
          success: response.success !== false,
          data: response.data || response,
        }

        this.showSuccess(response.message || 'تم تنفيذ الاستيراد بنجاح')
      } catch (error) {
        this.importResult = {
          success: false,
          error: error.response?.data?.message || error.message || 'حدث خطأ أثناء الاستيراد',
          data: error.response?.data?.data || null,
        }
        this.showError(this.importResult.error)
      } finally {
        this.loading = false
      }
    },

    resetForm() {
      this.importFile = null
      this.dryRun = false
      this.fileError = null
      this.importResult = null
      this.staticFields = {
        technical_responsible_id: null,
        office_agreement_responsible_id: null,
        office_drawing_responsible_id: null,
        inspection_teams: [],
        actual_cost: null,
        driver_cost: null,
        authorized_client_id: null,
      }
      const fileInput = document.getElementById('file-input')
      if (fileInput) fileInput.value = ''
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return `${Math.round(bytes / Math.pow(k, i) * 100) / 100} ${sizes[i]}`
    },

    formatDuration(seconds) {
      if (!seconds) return '0 ثانية'
      if (seconds < 60) {
        return `${Math.round(seconds * 10) / 10} ثانية`
      }
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = Math.round(seconds % 60)
      return `${minutes} دقيقة ${remainingSeconds} ثانية`
    },

    openPortalImportModal() {
      if (!this.canImportFromPortal) return
      this.portalImportFailure = null
      this.showPortalImportModal = true
    },

    clearPortalSessionId() {
      this.portalSessionId = ''
    },

    clearStalePortalImportHint() {
      try {
        sessionStorage.removeItem('landsurvey_portal_import_running')
        sessionStorage.removeItem('landsurvey_portal_import_total')
      } catch (e) {
        // ignore
      }
    },

    setPortalImportRunning(running, total = null) {
      this.portalImportRunning = running
      if (total != null) this.portalQueuedTotal = total
      if (!running) this.portalQueuedTotal = null
    },

    buildPortalPayload() {
      const payload = {
        sessionid: String(this.portalSessionId || '').trim(),
        import_all: true,
      }
      const s = this.staticFields
      if (s.technical_responsible_id) payload.technical_responsible_id = s.technical_responsible_id
      if (s.office_agreement_responsible_id) payload.office_agreement_responsible_id = s.office_agreement_responsible_id
      if (s.office_drawing_responsible_id) payload.office_drawing_responsible_id = s.office_drawing_responsible_id
      if (s.authorized_client_id) payload.authorized_client_id = s.authorized_client_id
      if (Array.isArray(s.inspection_teams) && s.inspection_teams.length > 0) {
        payload.inspection_teams = s.inspection_teams.filter(id => id)
      }
      const hasActualCost = s.actual_cost !== null && s.actual_cost !== '' && typeof s.actual_cost !== 'undefined'
      const hasDriverCost = s.driver_cost !== null && s.driver_cost !== '' && typeof s.driver_cost !== 'undefined'
      if (hasActualCost) payload.actual_cost = s.actual_cost
      if (hasDriverCost) payload.driver_cost = s.driver_cost
      return payload
    },

    async handlePortalImport() {
      if (!this.canImportFromPortal) {
        this.showError('ليست لديك صلاحية الاستيراد من البوابة')
        return
      }

      const sessionid = String(this.portalSessionId || '').trim()
      if (!sessionid) {
        this.showError('يرجى لصق معرّف جلسة البوابة (sessionid)')
        return
      }

      this.portalSubmitting = true
      this.portalImportFailure = null

      try {
        const response = await landsurveyOrdersAPI.importFromPortal(this.buildPortalPayload())
        const responseData = response.data || response
        const total = responseData.totalrowcount
        this.setPortalImportRunning(true, total || null)
        this.showPortalImportModal = false
        this.clearPortalSessionId()

        const countText = total ? `${total}` : 'كل'
        this.showSuccess(
          `بدأ الاستيراد من البوابة. سيتم سحب ${countText} سجل. ستصلك إشعار عند الانتهاء.`,
        )
      } catch (error) {
        const status = error.response?.status
        const message = error.response?.data?.message
          || error.message
          || 'حدث خطأ أثناء الاستيراد من البوابة'

        if (status === 422) {
          this.showError(`${message} يرجى لصق sessionid جديد من البوابة.`)
        } else {
          this.showError(message)
        }
      } finally {
        this.portalSubmitting = false
      }
    },

    parseJsonMaybe(value) {
      if (value == null || typeof value === 'object') return value
      if (typeof value !== 'string') return value
      try {
        return JSON.parse(value)
      } catch (e) {
        return value
      }
    },

    parseNotificationData(notification) {
      let nested = notification && (notification.data || notification.payload)
        ? (notification.data || notification.payload)
        : {}
      nested = this.parseJsonMaybe(nested) || {}
      const merged = {
        ...(notification || {}),
        ...nested,
      }
      if (merged.report) merged.report = this.parseJsonMaybe(merged.report)
      return merged
    },

    notificationTypeCandidates(notification) {
      const data = this.parseNotificationData(notification)
      return [
        notification && notification.type,
        notification && notification.notification_type,
        data.notification_type,
        data.event,
        data.type,
      ].filter(Boolean).map(t => this.normalizeNotificationType(t))
    },

    normalizeNotificationType(type) {
      const last = String(type).replace(/\\/g, '/').split('/').pop()
      return last.replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/-/g, '_').toLowerCase()
    },

    matchesNotificationType(notification, expected) {
      const wanted = this.normalizeNotificationType(expected)
      return this.notificationTypeCandidates(notification).some(t => t === wanted || t.includes(wanted))
    },

    isPortalFailureNotification(notification) {
      if (!this.matchesNotificationType(notification, PORTAL_FAILED_TYPE)) return false
      const data = this.parseNotificationData(notification)
      const payloadType = data.type || (data.report && data.report.type)
      const source = data.source || (data.report && data.report.source)
      if (payloadType === 'landsurvey_orders' || source === 'landsurvey_portal') return true
      return this.portalImportRunning && payloadType !== 'fees' && payloadType !== 'requests'
    },

    isPortalCompletedNotification(notification) {
      if (this.matchesNotificationType(notification, PORTAL_FAILED_TYPE)) return false
      if (this.matchesNotificationType(notification, PORTAL_COMPLETED_TYPE)) return true
      const data = this.parseNotificationData(notification)
      const source = data.source || (data.report && data.report.source)
      const hasReport = Boolean(data.import || (data.report && data.report.import))
      return source === 'landsurvey_portal' && hasReport
    },

    normalizeErrorDetails(raw) {
      if (!raw) return { count: 0, items: [], truncated: false }
      if (Array.isArray(raw)) {
        return {
          count: raw.length,
          items: raw.slice(0, 25),
          truncated: raw.length > 25,
        }
      }
      const items = Array.isArray(raw.items) ? raw.items : []
      return {
        count: Number(raw.count) || items.length,
        items: items.slice(0, 25),
        truncated: Boolean(raw.truncated) || items.length > 25,
      }
    },

    extractPortalReport(notification) {
      const data = this.parseNotificationData(notification)
      const nested = (data.report && typeof data.report === 'object') ? data.report : {}
      return {
        type: data.type || nested.type,
        source: data.source || nested.source,
        duration_seconds: data.duration_seconds != null ? data.duration_seconds : nested.duration_seconds,
        import: data.import || nested.import || {},
        projects: data.projects || nested.projects || {},
        geodesias: data.geodesias || nested.geodesias || {},
      }
    },

    consumePortalImportNotifications(items) {
      if (!Array.isArray(items) || items.length === 0) return

      items.forEach(notification => {
        const id = notification && (notification.id || notification.uuid)
        if (id && this.processedPortalNotificationIds[id]) return

        const unread = notification && notification.is_read === false
        const shouldHandle = this.portalImportRunning || unread
        if (!shouldHandle) return

        if (this.isPortalFailureNotification(notification)) {
          if (id) this.$set(this.processedPortalNotificationIds, id, true)
          this.applyPortalFailedNotification(notification)
        } else if (this.isPortalCompletedNotification(notification)) {
          if (id) this.$set(this.processedPortalNotificationIds, id, true)
          this.applyPortalCompletedNotification(notification)
        }
      })
    },

    applyPortalCompletedNotification(notification) {
      this.setPortalImportRunning(false)
      this.portalImportFailure = null
      this.portalReport = this.extractPortalReport(notification)
      this.portalReportTitle = notification.title || 'اكتمل الاستيراد من البوابة'
      this.portalReportMessage = notification.message || this.parseNotificationData(notification).message || ''
      this.refreshAfterPortalImport()
    },

    applyPortalFailedNotification(notification) {
      this.setPortalImportRunning(false)
      this.portalReport = null
      const data = this.parseNotificationData(notification)
      this.portalImportFailure = notification.message
        || data.message
        || 'فشل الاستيراد من البوابة'
      this.showError(this.portalImportFailure)
    },

    refreshAfterPortalImport() {
      landsurveyOrdersAPI.list().catch(() => {})
      this.$store.dispatch('projects/listProjects', { query: {} }).catch(() => {})
    },

    showSuccess(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'نجاح',
          icon: 'CheckCircleIcon',
          variant: 'success',
          text: message,
        },
      })
    },

    showError(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'خطأ',
          icon: 'AlertCircleIcon',
          variant: 'danger',
          text: message,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.bg-light-primary {
  background-color: rgba(115, 103, 240, 0.1) !important;
}
.bg-light-success {
  background-color: rgba(40, 199, 111, 0.1) !important;
}
.bg-light-info {
  background-color: rgba(0, 207, 232, 0.1) !important;
}
.bg-light-warning {
  background-color: rgba(255, 159, 67, 0.1) !important;
}
.bg-light-danger {
  background-color: rgba(234, 84, 85, 0.1) !important;
}
.bg-light-secondary {
  background-color: rgba(168, 170, 174, 0.1) !important;
}
</style>
